import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { Business, PersonAdd, Search } from '@mui/icons-material';
import ValidadoLogo from '../components/ValidadoLogo';

interface Company {
  _id: string;
  name: string;
  industry: string;
  size: string;
  totalEmployees: number;
  logo?: {
    url: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`registration-tabpanel-${index}`}
      aria-labelledby={`registration-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const EnhancedRegisterPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchCompany, setSearchCompany] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  // Form data for new company registration
  const [newCompanyForm, setNewCompanyForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    companyEmail: '',
    phone: '',
    industry: '',
    size: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    }
  });

  // Form data for joining existing company
  const [joinCompanyForm, setJoinCompanyForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    message: ''
  });

  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing',
    'Retail', 'Construction', 'Transportation', 'Real Estate', 'Other'
  ];

  const companySizes = [
    '1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'
  ];

  const steps = ['Personal Info', 'Company Details', 'Review & Submit'];

  useEffect(() => {
    if (tabValue === 1) {
      fetchCompanies();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, searchCompany]);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`/api/auth/companies?search=${searchCompany}`);
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
  };

  const handleNewCompanySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newCompanyForm.password !== newCompanyForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/register-company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: newCompanyForm.firstName,
          lastName: newCompanyForm.lastName,
          email: newCompanyForm.email,
          password: newCompanyForm.password,
          companyName: newCompanyForm.companyName,
          companyEmail: newCompanyForm.companyEmail,
          phone: newCompanyForm.phone,
          industry: newCompanyForm.industry,
          size: newCompanyForm.size,
          address: newCompanyForm.address
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Company and admin account created successfully! You can now login.');
        // Reset form
        setNewCompanyForm({
          firstName: '', lastName: '', email: '', password: '', confirmPassword: '',
          companyName: '', companyEmail: '', phone: '', industry: '', size: '',
          address: { street: '', city: '', state: '', zipCode: '', country: 'United States' }
        });
        setActiveStep(0);
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCompanySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCompany) {
      setError('Please select a company to join');
      return;
    }

    if (joinCompanyForm.password !== joinCompanyForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/request-join-company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: joinCompanyForm.firstName,
          lastName: joinCompanyForm.lastName,
          email: joinCompanyForm.email,
          password: joinCompanyForm.password,
          companyId: selectedCompany._id,
          role: joinCompanyForm.role,
          message: joinCompanyForm.message
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Join request submitted successfully! Please wait for approval from company administrators.');
        // Reset form
        setJoinCompanyForm({
          firstName: '', lastName: '', email: '', password: '', confirmPassword: '',
          role: 'user', message: ''
        });
        setSelectedCompany(null);
      } else {
        setError(data.message || 'Join request failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderNewCompanyStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={newCompanyForm.firstName}
                onChange={(e) => setNewCompanyForm({...newCompanyForm, firstName: e.target.value})}
                required
              />
              <TextField
                fullWidth
                label="Last Name"
                value={newCompanyForm.lastName}
                onChange={(e) => setNewCompanyForm({...newCompanyForm, lastName: e.target.value})}
                required
              />
            </Box>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newCompanyForm.email}
              onChange={(e) => setNewCompanyForm({...newCompanyForm, email: e.target.value})}
              required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={newCompanyForm.password}
                onChange={(e) => setNewCompanyForm({...newCompanyForm, password: e.target.value})}
                required
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={newCompanyForm.confirmPassword}
                onChange={(e) => setNewCompanyForm({...newCompanyForm, confirmPassword: e.target.value})}
                required
              />
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Company Name"
              value={newCompanyForm.companyName}
              onChange={(e) => setNewCompanyForm({...newCompanyForm, companyName: e.target.value})}
              required
            />
            <TextField
              fullWidth
              label="Company Email"
              type="email"
              value={newCompanyForm.companyEmail}
              onChange={(e) => setNewCompanyForm({...newCompanyForm, companyEmail: e.target.value})}
              required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={newCompanyForm.phone}
                onChange={(e) => setNewCompanyForm({...newCompanyForm, phone: e.target.value})}
                required
              />
              <FormControl fullWidth required>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={newCompanyForm.industry}
                  onChange={(e) => setNewCompanyForm({...newCompanyForm, industry: e.target.value})}
                  label="Industry"
                >
                  {industries.map((industry) => (
                    <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <FormControl fullWidth required>
              <InputLabel>Company Size</InputLabel>
              <Select
                value={newCompanyForm.size}
                onChange={(e) => setNewCompanyForm({...newCompanyForm, size: e.target.value})}
                label="Company Size"
              >
                {companySizes.map((size) => (
                  <MenuItem key={size} value={size}>{size} employees</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Review Your Information</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography><strong>Admin User:</strong> {newCompanyForm.firstName} {newCompanyForm.lastName}</Typography>
                <Typography><strong>Email:</strong> {newCompanyForm.email}</Typography>
              </Box>
              <Box>
                <Typography><strong>Company:</strong> {newCompanyForm.companyName}</Typography>
                <Typography><strong>Industry:</strong> {newCompanyForm.industry}</Typography>
                <Typography><strong>Size:</strong> {newCompanyForm.size} employees</Typography>
              </Box>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
              <ValidadoLogo width={200} height={80} />
              <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 2 }}>
                Join Valida.do
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                Create a new company or join an existing one
              </Typography>
            </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab icon={<Business />} label="Create New Company" />
          <Tab icon={<PersonAdd />} label="Join Existing Company" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box component="form" onSubmit={handleNewCompanySubmit}>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {renderNewCompanyStep()}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                Back
              </Button>
              {activeStep === steps.length - 1 ? (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Company'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box component="form" onSubmit={handleJoinCompanySubmit}>
             <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
               <Box>
                 <Box display="flex" alignItems="center" gap={2} mb={2}>
                   <TextField
                     fullWidth
                     label="Search Companies"
                     value={searchCompany}
                     onChange={(e) => setSearchCompany(e.target.value)}
                     InputProps={{
                       endAdornment: (
                         <Button onClick={fetchCompanies} size="small">
                           <Search />
                         </Button>
                       )
                     }}
                   />
                 </Box>
                 
                 {companies.length > 0 && (
                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                     {companies.map((company) => (
                       <Card 
                         key={company._id}
                         sx={{ 
                           cursor: 'pointer',
                           border: selectedCompany?._id === company._id ? 2 : 1,
                           borderColor: selectedCompany?._id === company._id ? 'primary.main' : 'grey.300'
                         }}
                         onClick={() => setSelectedCompany(company)}
                       >
                         <CardContent>
                           <Typography variant="h6">{company.name}</Typography>
                           <Typography color="text.secondary">{company.industry}</Typography>
                           <Box mt={1}>
                             <Chip label={`${company.size} employees`} size="small" />
                             <Chip label={`${company.totalEmployees} total`} size="small" sx={{ ml: 1 }} />
                           </Box>
                         </CardContent>
                       </Card>
                     ))}
                   </Box>
                 )}
               </Box>

               {selectedCompany && (
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                   <Alert severity="info">
                     You selected: <strong>{selectedCompany.name}</strong>
                   </Alert>
                   
                   <Box sx={{ display: 'flex', gap: 2 }}>
                     <TextField
                       fullWidth
                       label="First Name"
                       value={joinCompanyForm.firstName}
                       onChange={(e) => setJoinCompanyForm({...joinCompanyForm, firstName: e.target.value})}
                       required
                     />
                     <TextField
                       fullWidth
                       label="Last Name"
                       value={joinCompanyForm.lastName}
                       onChange={(e) => setJoinCompanyForm({...joinCompanyForm, lastName: e.target.value})}
                       required
                     />
                   </Box>
                   
                   <TextField
                     fullWidth
                     label="Email"
                     type="email"
                     value={joinCompanyForm.email}
                     onChange={(e) => setJoinCompanyForm({...joinCompanyForm, email: e.target.value})}
                     required
                   />
                   
                   <Box sx={{ display: 'flex', gap: 2 }}>
                     <TextField
                       fullWidth
                       label="Password"
                       type="password"
                       value={joinCompanyForm.password}
                       onChange={(e) => setJoinCompanyForm({...joinCompanyForm, password: e.target.value})}
                       required
                     />
                     <TextField
                       fullWidth
                       label="Confirm Password"
                       type="password"
                       value={joinCompanyForm.confirmPassword}
                       onChange={(e) => setJoinCompanyForm({...joinCompanyForm, confirmPassword: e.target.value})}
                       required
                     />
                   </Box>
                   
                   <FormControl fullWidth>
                     <InputLabel>Requested Role</InputLabel>
                     <Select
                       value={joinCompanyForm.role}
                       onChange={(e) => setJoinCompanyForm({...joinCompanyForm, role: e.target.value})}
                       label="Requested Role"
                     >
                       <MenuItem value="user">User</MenuItem>
                       <MenuItem value="manager">Manager</MenuItem>
                     </Select>
                   </FormControl>
                   
                   <TextField
                     fullWidth
                     label="Message to Company (Optional)"
                     multiline
                     rows={3}
                     value={joinCompanyForm.message}
                     onChange={(e) => setJoinCompanyForm({...joinCompanyForm, message: e.target.value})}
                     placeholder="Tell the company why you want to join..."
                   />
                   
                   <Button
                     type="submit"
                     variant="contained"
                     fullWidth
                     size="large"
                     disabled={loading}
                   >
                     {loading ? 'Submitting Request...' : 'Request to Join Company'}
                   </Button>
                 </Box>
               )}
             </Box>
          </Box>
        </TabPanel>

        <Box textAlign="center" mt={3}>
          <Typography variant="body2">
            Already have an account?{' '}
            <Button href="/login" variant="text">
              Sign In
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default EnhancedRegisterPage;